'use client';

import { c as _c } from "react/compiler-runtime";
import { computeSegmentScaleAxisBounds } from '@/domains/pages/shared/chart-axis';
import { SegmentScaleChart as PageSegmentScaleChart, SEGMENT_SCALE_PLOT_HEIGHT_PX, SegmentScaleChartSkeleton } from '@/domains/pages/shared/segment-scale-chart';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import type { SegmentSummary } from '@/domains/pages/ai-proficiency/utils/map-segment-aggregates';
import { segmentScoreColor } from '@/domains/pages/ai-proficiency/utils/segment-score-color';
import { formatScore } from '@/utils/format.util';
import { useCallback, useMemo } from 'react';
export { SEGMENT_SCALE_PLOT_HEIGHT_PX, SegmentScaleChartSkeleton };
type SegmentScaleChartProps = {
  segments: SegmentSummary[];
  median: number;
  orgScore: number | null;
  selectedName: string;
  onSelect?: (name: string) => void;
  className?: string;
};
const formatScoreValue = (value: number) => formatScore(value, {
  decimals: 1
});

/** Peer proficiency scores on a single axis, sized by headcount and split at the org score. */
export function SegmentScaleChart(t0) {
  const $ = _c(17);
  const {
    segments,
    median,
    orgScore,
    selectedName,
    onSelect,
    className
  } = t0;
  let t1;
  if ($[0] !== segments) {
    t1 = segments.map(_temp);
    $[0] = segments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scalePoints = t1;
  let t2;
  if ($[2] !== orgScore || $[3] !== segments) {
    const values = segments.map(_temp2).filter(_temp3);
    if (orgScore != null) {
      values.push(orgScore);
    }
    t2 = computeSegmentScaleAxisBounds(values);
    $[2] = orgScore;
    $[3] = segments;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const bounds = t2;
  let t3;
  if ($[5] !== median) {
    t3 = value => segmentScoreColor(value, median);
    $[5] = median;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const colorForValue = t3;
  const buildTooltipRows = _temp4;
  let t4;
  if ($[7] !== orgScore) {
    t4 = orgScore != null ? {
      value: orgScore,
      label: "org"
    } : null;
    $[7] = orgScore;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== bounds || $[10] !== className || $[11] !== colorForValue || $[12] !== onSelect || $[13] !== scalePoints || $[14] !== selectedName || $[15] !== t4) {
    t5 = <PageSegmentScaleChart segments={scalePoints} bounds={bounds} selectedName={selectedName} colorForValue={colorForValue} formatValue={formatScoreValue} formatTick={_temp5} buildTooltipRows={buildTooltipRows} metricLabel="score" reference={t4} onSelect={onSelect} className={className} />;
    $[9] = bounds;
    $[10] = className;
    $[11] = colorForValue;
    $[12] = onSelect;
    $[13] = scalePoints;
    $[14] = selectedName;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp5(tick) {
  return formatScore(tick, {
    decimals: Number.isInteger(tick) ? 0 : 1
  });
}
function _temp4(point) {
  return [{
    label: "AI proficiency",
    value: formatScoreValue(point.value)
  }, {
    label: "Users",
    value: formatUserCount(point.weight)
  }];
}
function _temp3(score) {
  return score != null;
}
function _temp2(segment_0) {
  return segment_0.score;
}
function _temp(segment) {
  return {
    name: segment.name,
    displayName: segment.displayName,
    value: segment.score,
    weight: segment.userCount
  };
}